// FLAG:useDocuAi — pure admission rule for the upload queue. Leaf module so the scheduling
// decision is unit-testable without the store, FileService or timers.
//
// See the plan section "Scheduler design (T2 decision)": the admission RULE is selected per queue
// item by its `source`. Receipt sources ('expense' | 'revenue') can only exist when
// isDmsReceiptUploadEnabled() is true, so with useDocuAi OFF the receipt branch is unreachable and
// the legacy branch below is the pre-existing condition verbatim.

export const RECEIPT_UPLOAD_SOURCES = ['expense', 'revenue']

export const isReceiptUploadSource = (source?: string): boolean =>
  RECEIPT_UPLOAD_SOURCES.includes(source ?? '')

type PendingRow = { finished?: boolean, error?: string | object }

/**
 * The LEGACY admission count, kept exactly as `processUploads` computed it before this change:
 * pending rows that are neither finished nor errored.
 *
 * Note what this does NOT count — and why the receipt path needs a different rule: a pending row
 * only exists after `prepare-upload` has RESPONDED. Between dequeuing a file and that response the
 * file is invisible here, so a poll-driven scheduler keeps admitting more work while prepares are
 * still in flight. Under load that is a positive feedback loop (RC1).
 */
export const countActivePendingUploads = (pending: PendingRow[] = []): number =>
  pending.filter(({ finished, error }) => !finished && !error).length

/**
 * Whether the next queued item may start.
 *
 * Receipt items are gated on `inFlightReceiptCount` — a count maintained by the caller that spans
 * the file's ENTIRE lifetime (prepare dispatched → terminal, complete-upload included), so
 * in-flight prepares and in-flight completes both hold their slot.
 */
export const canAdmitUpload = ({
  source,
  pending = [],
  inFlightReceiptCount = 0,
  limit,
}: {
  source?: string,
  pending?: PendingRow[],
  inFlightReceiptCount?: number,
  limit: number,
}): boolean => {
  if (isReceiptUploadSource(source)) {
    return inFlightReceiptCount < limit
  }
  return countActivePendingUploads(pending) < limit
}

/**
 * Index of the first queue item that may start, or -1.
 *
 * Scanning past a blocked item avoids head-of-line blocking when a receipt batch and a Documents
 * upload share the queue (only possible with useDocuAi ON). For a queue of legacy items only —
 * i.e. every flag-OFF case — every item is judged by the same legacy condition, so this returns 0
 * exactly when the previous `queuedUploads.length && pendingUploadCount < LIMIT` test passed, and
 * -1 exactly when it did not. Same item, same decision.
 */
export const findAdmissibleUploadIndex = ({
  queue = [],
  pending = [],
  inFlightReceiptCount = 0,
  limit,
}: {
  queue?: Array<{ source?: string }>,
  pending?: PendingRow[],
  inFlightReceiptCount?: number,
  limit: number,
}): number => queue.findIndex((item) => canAdmitUpload({
  source: item?.source,
  pending,
  inFlightReceiptCount,
  limit,
}))
